import { atom } from 'jotai';
import { createApiClient } from '@/services/api/httpClient';
import { TokenService } from '@/services/auth/tokenService';

interface FetchDollhouseUrlParams {
  machineId: string;
}

interface FetchVideoUrlParams {
  videoKey: string;
}

interface PresignUrlResponse {
  url: string;
}

/**
 * Callable atom to fetch the presigned dollhouse URL
 * Usage: const fetchUrl = useSetAtom(fetchDollhousePresignUrlAtom);
 *        const result = await fetchUrl({ machineId: 'machine_123' });
 */
export const fetchDollhousePresignUrlAtom = atom(
  null,
  async (_get, _set, params: FetchDollhouseUrlParams) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    if (!params.machineId) {
      throw new Error('Machine ID is required');
    }

    const url = `/presign?key=${params.machineId}_dollhouse_webgl/${params.machineId}&role=viewer&artifact=dollhouse`;
    
    const apiClient = createApiClient(
      () => accessToken,
      process.env.PUBLIC_PRESIGN_API_URL,
    );
    
    const response = await apiClient.get<PresignUrlResponse>(url);
    
    return response.url;
  }
);

/**
 * Callable atom to fetch the presigned video URL
 * Usage: const fetchVideoUrl = useSetAtom(fetchVideoPresignUrlAtom);
 *        const result = await fetchVideoUrl({ videoKey: 'path/to/video.mp4' });
 */
export const fetchVideoPresignUrlAtom = atom(
  null,
  async (_get, _set, params: FetchVideoUrlParams) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    if (!params.videoKey) {
      throw new Error('Video key is required');
    }

    const url = `/presign?key=${params.videoKey}`;
    
    const apiClient = createApiClient(
      () => accessToken,
      process.env.PUBLIC_PRESIGN_API_URL,
    );
    
    const response = await apiClient.get<PresignUrlResponse>(url);
    
    return response.url;
  }
);

